// frontend/src/components/Settings.jsx - FULLY TRANSLATED VERSION
import React, { useState, useEffect } from 'react';
import { User, CreditCard, Bell, Shield, Save, Eye, EyeOff, CheckCircle, Crown, Zap, Star } from 'lucide-react';
import { api } from '../services/api';
import { useNotification } from './NotificationContext';
import LanguageSwitcher from './LanguageSwitcher';
import Payment from './Payment';
import { useLanguage } from '../contexts/LanguageContext';

const Settings = ({ user }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const { addToast } = useNotification();
  const { t, language } = useLanguage();

   const SUBSCRIPTION_PLANS = {
  basic: {
    name: 'Basic',
    price: 12,
    rendersPerMonth: 8,
    stripePriceId: process.env.STRIPE_BASIC_PRICE_ID,
    features: [
      t('rendersPerMonth8'),
      t('resolution1080p'), 
      t('standardQueue'),
      t('emailSupport')
    ],
    icon: <Star className="w-6 h-6" />,
    color: 'from-blue-600 to-blue-700',
    popular: false
  },
  pro: {
    name: 'Pro',
    price: 25, 
    rendersPerMonth: 20,
    stripePriceId: process.env.STRIPE_PRO_PRICE_ID,
    features: [
      t('rendersPerMonth20'),
      t('resolution4K'),
      t('priorityQueue'),
      t('prioritySupport')
    ],
    icon: <Crown className="w-6 h-6" />,
    color: 'from-purple-600 to-purple-700',
    popular: true
  },
  studio: {
    name: 'Studio',
    price: 50,
    rendersPerMonth: 35,
    stripePriceId: process.env.STRIPE_STUDIO_PRICE_ID,
    features: [
      t('rendersPerMonth35'),
      t('resolution4KPlus'),
      t('expressQueueNextInLine'),
      t('dedicatedSupport')
    ],
    icon: <Zap className="w-6 h-6" />,
    color: 'from-red-600 to-red-700',
    popular: false
  }
};
  // Profile form state
  const [profileData, setProfileData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    youtubeChannel: user?.youtubeChannel || ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Update profile data when user prop changes
  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        youtubeChannel: user.youtubeChannel || ''
      });
    }
  }, [user]);

  useEffect(() => {
    // If no user prop passed, fetch profile directly
    const loadIfMissing = async () => {
      if (!user) {
        try {
          const res = await api.getUserProfile();
          if (res?.success && res.data) {
            const profile = res.data.user || res.data;
            setProfileData(prev => ({
              ...prev,
              firstName: profile.firstName || prev.firstName,
              lastName: profile.lastName || prev.lastName,
              email: profile.email || prev.email,
              youtubeChannel: profile.youtubeChannel || prev.youtubeChannel
            }));
          }
        } catch (err) {
          console.error('Failed to load profile in Settings:', err);
        }
      }
    };
    loadIfMissing();
  }, [user]);

  // Handle profile form changes
  const handleProfileChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  // Handle password form changes
  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  // Save profile updates
  const handleProfileSave = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await api.updateUserProfile(profileData);
      if (response.success) {
        addToast(t('profileUpdatedSuccessfully'), 'success');
      }
    } catch (error) {
      addToast(`Failed to update profile: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Update password
  const handlePasswordSave = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      addToast(t('newPasswordsDoNotMatch'), 'error');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      addToast(t('passwordMustBe6Chars'), 'error');
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      if (response.success) {
        addToast(t('passwordUpdatedSuccessfully'), 'success');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      addToast(`Failed to update password: ${error.message}`, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle subscription upgrade
  const handleUpgrade = (planKey) => {
    const plan = SUBSCRIPTION_PLANS[planKey];
    setSelectedPlan({
      id: planKey,
      name: plan.name,
      price: plan.price
    });
    setShowPayment(true);
  };

  // Handle successful payment
  const handlePaymentSuccess = () => {
    setShowPayment(false);
    setSelectedPlan(null);
    addToast(t('subscriptionUpgradedSuccessfully'), 'success');
    // Refresh the page to update user data
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  // Handle payment cancellation
  const handlePaymentCancel = () => {
    setShowPayment(false);
    setSelectedPlan(null);
  };

  // Get current user plan
  const getCurrentPlan = () => {
    const userPlan = user?.subscriptionPlan || 'Free';
    return userPlan.toLowerCase();
  };

  // Tab navigation
  const tabs = [
    { id: 'profile', name: t('profile'), icon: User },
    { id: 'subscription', name: t('subscription'), icon: CreditCard },
    { id: 'preferences', name: t('preferences'), icon: Bell },
    { id: 'security', name: t('security'), icon: Shield }
  ];

  // Show payment modal if selected
  if (showPayment && selectedPlan) {
    return (
      <Payment
        planId={selectedPlan.id}
        planName={selectedPlan.name}
        amount={selectedPlan.price}
        userEmail={user?.email}
        onSuccess={handlePaymentSuccess}
        onCancel={handlePaymentCancel}
      />
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-white mb-8">{t('settings')}</h1>
        
        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-8 bg-gray-800 p-1 rounded-lg">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                  activeTab === tab.id
                    ? 'bg-red-600 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
              >
                <Icon size={18} />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-6">{t('profileInformation')}</h2>
            
            <form onSubmit={handleProfileSave} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('firstName')}
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={profileData.firstName}
                    onChange={handleProfileChange}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('lastName')}
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={profileData.lastName}
                    onChange={handleProfileChange}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    onChange={handleProfileChange}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('youtubeChannel')} ({t('optional')})
                  </label>
                  <input
                    type="text"
                    name="youtubeChannel"
                    value={profileData.youtubeChannel}
                    onChange={handleProfileChange}
                    placeholder={t('channelUrlPlaceholder')}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  <Save size={18} />
                  <span>{isLoading ? t('saving') : t('saveChanges')}</span>
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Subscription Tab - UPDATED WITH NEW MODEL */}
        {activeTab === 'subscription' && (
          <div className="space-y-6">
            {/* Current Plan Status */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-white mb-4">{t('currentPlan')}</h2>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-medium text-xl">
                    {user?.subscriptionPlan || 'Free'} {t('plan')}
                  </h3>
                  <p className="text-gray-400 text-sm mt-1">
                    {user?.rendersAvailable || 0} {t('rendersRemainingThisMonth')}
                  </p>
                  {user?.rendersReset && (
                    <p className="text-gray-500 text-xs mt-1">
                      {t('resetsOn')} {new Date(user.rendersReset).toLocaleDateString()}
                    </p>
                  )}
                </div>
                {user?.subscriptionPlan !== 'Free' && (
                  <div className="text-right">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-900/30 text-green-400">
                      ✓ {t('active')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Available Plans - NEW MODEL */}
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-lg font-semibold text-white mb-6">{t('availablePlans')}</h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {Object.entries(SUBSCRIPTION_PLANS).map(([planKey, plan]) => {
                  const isCurrentPlan = getCurrentPlan() === planKey;
                  
                  return (
                    <div key={planKey} className={`bg-gray-700 rounded-lg p-6 border transition-colors relative ${
                      plan.popular ? 'border-purple-500 ring-2 ring-purple-500/20' : 'border-gray-600 hover:border-gray-500'
                    } ${isCurrentPlan ? 'opacity-75' : ''}`}>
                      
                      {/* Popular Badge */}
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                          ⭐ {t('mostPopular')}
                        </div>
                      )}

                      {/* Current Plan Badge */}
                      {isCurrentPlan && (
                        <div className="absolute -top-3 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                          ✓ {t('current')}
                        </div>
                      )}

                      <div className="text-center mb-4">
                        <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${plan.color} text-white mb-3`}>
                          {plan.icon}
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">{plan.name} {t('plan')}</h3>
                        <div className="mb-3">
                          <span className="text-3xl font-bold text-white">${plan.price}</span>
                          <span className="text-gray-400 ml-1">{t('month')}</span>
                        </div>
                        <p className="text-gray-400 text-sm">
                          ${(plan.price / plan.rendersPerMonth).toFixed(2)} {t('perRender')}
                        </p>
                      </div>

                      <ul className="space-y-2 mb-6">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-center text-gray-300">
                            <CheckCircle size={16} className="text-green-500 mr-2" />
                            <span className="text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <button
                        onClick={() => handleUpgrade(planKey)}
                        disabled={isCurrentPlan}
                        className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                          isCurrentPlan
                            ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                            : `bg-gradient-to-r ${plan.color} text-white hover:opacity-90`
                        }`}
                      >
                        {isCurrentPlan ? t('currentPlanButton') : `${t('upgradeTo')} ${plan.name}`}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Payment Methods Info */}
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
              <h4 className="text-blue-400 font-semibold mb-2 flex items-center">
                <span className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-white text-xs font-bold mr-2">₮</span>
                {t('multiplePaymentOptions')}
              </h4>
              <p className="text-blue-200 text-sm">
                {t('paymentOptionsDesc')}
              </p>
            </div>
          </div>
        )}

        {/* Preferences Tab */}
        {activeTab === 'preferences' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-6">{t('preferences')}</h2>
            
            <div className="space-y-6">
              {/* Language Setting */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  {t('language')} / 语言
                </label>
                <div className="flex items-center space-x-4">
                  <LanguageSwitcher variant="button" />
                  <span className="text-gray-400 text-sm">
                    {t('currentLanguage')}: {language === 'en' ? 'English' : '中文'}
                  </span>
                </div>
              </div>

              {/* Email Notifications */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  {t('emailNotifications')}
                </label>
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="rounded border-gray-600 text-red-600 focus:ring-red-500 bg-gray-700"
                    />
                    <span className="ml-2 text-gray-300">{t('renderCompletionNotifications')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      defaultChecked
                      className="rounded border-gray-600 text-red-600 focus:ring-red-500 bg-gray-700"
                    />
                    <span className="ml-2 text-gray-300">{t('weeklyUsageReports')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      className="rounded border-gray-600 text-red-600 focus:ring-red-500 bg-gray-700"
                    />
                    <span className="ml-2 text-gray-300">{t('marketingUpdates')}</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end">
                <button className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors">
                  <Save size={18} />
                  <span>{t('savePreferences')}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div className="bg-gray-800 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-white mb-6">{t('securitySettings')}</h2>
            
            <form onSubmit={handlePasswordSave} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('currentPassword')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-2.5 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('newPassword')}
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  minLength="6"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('confirmNewPassword')}
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  minLength="6"
                  required
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  <Save size={18} />
                  <span>{isLoading ? t('updating') : t('updatePassword')}</span>
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;